import { UserInfoDto } from '@growdash/identity/generated';
import React, { useEffect, useState } from 'react';
import { createGrowintelService, GrowIntelService } from '../client';
import RouteHandler from '../components/hocs/route-handler';
import { AreaWithCity, Brand } from '../generated';
import useWatchlist from '../hooks/useWatchlist';
import { AishaService, initializeAishaService } from '../lib/aisha';
import { IdentityService, initializeIdentityService } from '../lib/identity';
import { InitializeGrowintel } from '../types/initialize-growintel';
import { GrowIntelContext } from './context';

export default function GrowIntelProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [growIntelService, setGrowintelService] =
    useState<GrowIntelService | null>(null);

  const [identityService, setIdentityService] =
    useState<IdentityService | null>(null);
  const [aishaService, setAishaService] = useState<AishaService | null>(null);
  const [growintelUser, setGrowintelUser] = useState<UserInfoDto | null>(null);
  const [clientBrands, setClientBrands] = useState<Brand[]>([]);
  const [refreshUser, setRefreshUser] = useState<() => void>(() => () => {});

  const [isMetaDataLoading, setIsMetaDataLoading] = useState(true);

  const [metadata, setMetadata] = useState<{
    areas: string[];
    cities: string[];
    cuisines: string[];
    areasWithCity: AreaWithCity[];
  }>({
    areas: [],
    cities: [],
    cuisines: [],
    areasWithCity: [],
  });

  const initializeGrowintel = (params: InitializeGrowintel) => {
    setRefreshUser(() => params.refreshUser || (() => {}));

    const service = createGrowintelService({
      accessToken: params.accessToken,
      service_url: params.service_urls.growintel,
    });

    const identityService = initializeIdentityService({
      accessToken: params.accessToken,
      service_url: params.service_urls.identity,
    });

    const aishaService = initializeAishaService({
      accessToken: params.accessToken,
      service_url: params.service_urls.aisha,
    });
    setGrowintelService(service);
    setAishaService(aishaService);
    setIdentityService(identityService);
  };

  const fetchUserInfo = async () => {
    if (!identityService) return;
    const { data: user } =
      await identityService.usersAPI.usersControllerGetUserInfo();
    setGrowintelUser(user);
  };

  const refreshGrowintelUser = async () => {
    if (typeof refreshUser === 'function') {
      await refreshUser();
    } else {
      console.warn('refreshUser is not a function');
    }
    // Also refetch the user info for GrowIntel context
    if (identityService) {
      await fetchUserInfo();
    }
  };

  useEffect(() => {
    if (identityService) {
      fetchUserInfo();
    }
  }, [identityService]);

  const fetchBrands = async () => {
    if (!growIntelService) return;
    const brandsRes = await growIntelService?.brandsAPI.getBrandsBrandsGet(
      1,
      Number.MAX_SAFE_INTEGER,
    );
    setClientBrands(brandsRes?.data.items ?? []);
  };

  const allItemsParams = [1, Number.MAX_SAFE_INTEGER];

  const fetchMetadata = async () => {
    if (!growIntelService) return;
    const { metaAPI } = growIntelService;
    setIsMetaDataLoading(true);

    const [areasRes, citiesRes, cuisinesRes, areasWithCityRes] =
      await Promise.all([
        metaAPI.getAreasMetaAreasGet(...allItemsParams),
        metaAPI.getCityMetaCitiesGet(...allItemsParams),
        metaAPI.getCuisinesMetaCuisinesGet(...allItemsParams),
        metaAPI.getAreasV2MetaV2AreasGet(...allItemsParams),
      ]);
    setIsMetaDataLoading(false);

    setMetadata({
      areas: areasRes?.data.items ?? [],
      cities: citiesRes?.data.items ?? [],
      cuisines: cuisinesRes?.data.items ?? [],
      areasWithCity: areasWithCityRes.data.items ?? [],
    });
  };

  const { refetchWatchlist } = useWatchlist();

  useEffect(() => {
    fetchBrands();
    fetchMetadata();
    refetchWatchlist();
  }, [growIntelService]);

  return (
    <GrowIntelContext.Provider
      value={{
        service: growIntelService,
        user: growintelUser,
        lib: {
          identityService: identityService,
          aishaService: aishaService,
        },
        config: {
          initializeGrowintel,
          refreshUser: refreshGrowintelUser,
        },
        metadata: metadata,
        isMetaDataLoading,
        brands: clientBrands,
      }}
    >
      <RouteHandler>{children}</RouteHandler>
    </GrowIntelContext.Provider>
  );
}
